import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import OptimizedImage from '../OptimizedImage/OptimizedImage';
import './Header.css';

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Add scroll event listener for sticky glass effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className={`asw-header ${scrolled ? 'header-scrolled' : ''}`}>
      <div className="asw-header-container">
        {/* Left: Brand Logo */}
        <div className="asw-header-logo">
          <Link to="/" className="asw-logo-link">
            <OptimizedImage 
              src="/Website-Images/Logo/logo.jpg" 
              alt="ASW Cars - Dealership & Finance" 
              loading="eager"
              priority={true}
              className="asw-logo-img"
            />
          </Link>
        </div>

        {/* Center: Main Navigation for Desktop */}
        <nav className="asw-nav-menu desktop-nav">
          <ul className="asw-nav-list">
            <li>
              <Link 
                to="/" 
                className={`asw-nav-link ${location.pathname === '/' ? 'active' : ''}`}
              >
                New Cars
              </Link>
            </li>
            <li>
              <Link 
                to="/finance-offers" 
                className={`asw-nav-link ${location.pathname === '/finance-offers' ? 'active' : ''}`}
              >
                Finance & Offers
              </Link>
            </li>
            <li>
              <Link 
                to="/business-loan" 
                className={`asw-nav-link ${location.pathname === '/business-loan' ? 'active' : ''}`}
              >
                Business Loan
              </Link>
            </li>
            <li>
              <Link 
                to="/blogs" 
                className={`asw-nav-link ${location.pathname.startsWith('/blogs') ? 'active' : ''}`}
              >
                Blogs
              </Link>
            </li>
            <li>
              <Link 
                to="/about-us" 
                className={`asw-nav-link ${location.pathname === '/about-us' ? 'active' : ''}`}
              >
                About Us
              </Link>
            </li>
          </ul>
        </nav>

        {/* Right: Actions & Helplines */}
        <div className="asw-header-actions">
          {/* Phone Helpline */}
          <a 
            href="tel:+919987828690" 
            className="asw-header-phone"
            title="Call ASW Helpline"
          >
            <span className="asw-phone-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
            </span>
            <div className="asw-phone-details">
              <span className="asw-phone-label">Direct Helpline</span>
              <span className="asw-phone-num">+91 99878 28690</span>
            </div>
          </a>

          {/* WhatsApp Direct Connect CTA */}
          <a 
            href="https://wa.me/919987828417?text=Hi%20ASW%20Cars,%20I%20want%20to%20know%20more%20about%20Car%20Deals%20and%20Finance." 
            target="_blank" 
            rel="noopener noreferrer"
            className="asw-header-btn-whatsapp"
            title="WhatsApp ASW Cars"
          >
            <i className="fa-brands fa-whatsapp"></i>
            <span>WhatsApp</span>
          </a>


          {/* Mobile Menu Toggle Button */}
          <button 
            type="button"
            className="asw-mobile-toggle" 
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
          >
            <span className={`asw-burger-bar ${mobileMenuOpen ? 'open' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile Drawer Navigation Menu */}
      <div className={`asw-mobile-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="asw-drawer-header">
          <img 
            src="/Website-Images/Logo/logo.jpg" 
            alt="ASW Cars" 
            className="asw-drawer-logo" 
          />
          <button 
            type="button"
            className="asw-drawer-close-btn" 
            onClick={toggleMobileMenu}
          >
            ✕
          </button>
        </div>

        <ul className="asw-drawer-links">
          <li>
            <Link 
              to="/" 
              className={location.pathname === '/' ? 'active' : ''}
              onClick={handleMobileLinkClick}
            >
              <i className="fa-solid fa-car"></i>
              <span>New Cars</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/finance-offers" 
              className={location.pathname === '/finance-offers' ? 'active' : ''}
              onClick={handleMobileLinkClick}
            >
              <i className="fa-solid fa-hand-holding-dollar"></i>
              <span>Finance & Offers</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/business-loan" 
              className={location.pathname === '/business-loan' ? 'active' : ''}
              onClick={handleMobileLinkClick}
            >
              <i className="fa-solid fa-briefcase"></i>
              <span>Business Loan</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/blogs" 
              className={location.pathname.startsWith('/blogs') ? 'active' : ''}
              onClick={handleMobileLinkClick}
            >
              <i className="fa-solid fa-newspaper"></i>
              <span>Blogs & Guides</span>
            </Link>
          </li>
          <li>
            <Link 
              to="/about-us" 
              className={location.pathname === '/about-us' ? 'active' : ''}
              onClick={handleMobileLinkClick}
            >
              <i className="fa-solid fa-building"></i>
              <span>About Us</span>
            </Link>
          </li>
        </ul>

        {/* Drawer Contact Box */}
        <div className="asw-drawer-footer">
          <a href="tel:+919987828690" className="drawer-action-btn phone">
            <i className="fa-solid fa-phone"></i>
            <span>Call Helpline</span>
          </a>
          <a 
            href="https://wa.me/919987828417" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="drawer-action-btn whatsapp"
          >
            <i className="fa-brands fa-whatsapp"></i>
            <span>WhatsApp</span>
          </a>
        </div>
      </div>

      {/* Backdrop overlay for mobile drawer */}
      {mobileMenuOpen && (
        <div className="asw-drawer-overlay" onClick={toggleMobileMenu}></div>
      )}
    </header>
  );
};

export default Header;